import { App, Button, Card, Col, Form, Modal, Row, Typography } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'translations/hooks';

import { SegmentHistory } from 'business/quality-control/pages/segment-history/hooks/data/types';
import { useSegmentV1ControllerUpdateDetails } from 'generated/apiComponents';
import { isUndefinedOrNull } from 'technical/utils/is-null-or-undefined';

import styles from './index.module.scss';
import { UpdateSegmentFormValues } from './types';
import { UpdateSegmentForm } from './update-segment-form';

interface UpdateSegmentModalProps {
  isModalVisible: boolean;
  closeModal: () => void;
  segment: SegmentHistory | null;
  refetchData: () => void;
  openAddManuelScanModal: (segment: SegmentHistory) => void;
}

export const UpdateSegmentModal: React.FC<UpdateSegmentModalProps> = ({
  isModalVisible,
  closeModal,
  segment,
  refetchData,
  openAddManuelScanModal,
}) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<UpdateSegmentFormValues>();

  const { mutate: updateSegment } = useSegmentV1ControllerUpdateDetails();

  const save = async (row: UpdateSegmentFormValues) => {
    updateSegment(
      {
        pathParams: {
          id: segment?.id ?? '',
        },
        body: {
          date: row.date.utc().toISOString(),
        },
      },
      {
        onSuccess: async () => {
          refetchData();
          closeModal();

          message.open({
            type: 'success',
            content: t(
              'qualityControl.segmentHistory.editModal.update.success',
            ),
          });
        },
        onError: () => {
          message.open({
            type: 'error',
            content: t('qualityControl.segmentHistory.editModal.update.error'),
          });
        },
      },
    );
  };

  const data = [
    {
      label: t(
        'qualityControl.segmentHistory.drawer.segmentInfos.serialNumber',
      ),
      value: segment?.serialNumber,
    },
    {
      label: t('qualityControl.segmentHistory.drawer.segmentInfos.ringNumber'),
      value: segment?.ringNumber ?? '-',
    },
    {
      label: t('qualityControl.segmentHistory.drawer.segmentInfos.segmentType'),
      value: segment?.segmentType ?? '-',
    },
  ];

  const buttons = () => {
    return !isUndefinedOrNull(segment) ? (
      <div className={styles.buttonsFooter}>
        <Button type="primary" onClick={form.submit}>
          {t('qualityControl.segmentHistory.editModal.cta.submit')}
        </Button>
        <Button onClick={() => openAddManuelScanModal(segment)}>
          {t('qualityControl.segmentHistory.editModal.cta.newManualScan')}
        </Button>
      </div>
    ) : null;
  };

  return (
    <Modal
      title={t('qualityControl.segmentHistory.editModal.title')}
      open={isModalVisible}
      onCancel={closeModal}
      destroyOnHidden
      footer={buttons}
    >
      {!isUndefinedOrNull(segment) ? (
        <>
          <Card className={styles.card}>
            <Row gutter={16} justify="start">
              {data.map((item, index) => (
                <Col key={index} span={8}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography.Text type="secondary">
                      {item.label}
                    </Typography.Text>
                    <Typography.Text>{item.value}</Typography.Text>
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
          {isModalVisible ? (
            <UpdateSegmentForm
              form={form}
              save={save}
              initialValues={{
                date: dayjs(segment?.lastScanDate)
                  .tz()
                  .second(0)
                  .millisecond(0),
              }}
            />
          ) : null}
        </>
      ) : null}
    </Modal>
  );
};
